/**
 * useProfileHomepage — 个人公开主页导航 composable
 * @description 为已认证的设计师/服务商提供"查看我的公开主页"能力
 *   - 通过身份认证状态 API 判断是否已认证（而非仅 role 字段）
 *   - 延迟检查以避免与页面组件的并发请求产生取消冲突
 *   - 缓存 profile ID，避免重复请求
 */
import { computed, ref, shallowRef } from 'vue'
import { useRouter } from 'vue-router'
import { identityApi } from '@/api/userCenter/identity'
import { useUserStorev2 as useUserStore } from '@/store/modules/userCenter'

// ==================== 模块级响应式状态 ====================
// 使用 shallowRef 确保 Vue 能追踪状态变化并触发 computed 重新求值

/** 身份状态是否已检查完成（响应式） */
const statusChecked = shallowRef(false)

/** 是否正在检查中 */
let _statusChecking = false

/** 设计师认证状态（响应式） */
const designerCertified = shallowRef(false)

/** 服务商认证状态（响应式） */
const manufacturerCertified = shallowRef(false)

/** 设计师 profile ID 缓存 */
let _designerProfileId: string | null = null

/** 服务商 profile ID 缓存 */
let _manufacturerProfileId: string | null = null

export function useProfileHomepage() {
  const router = useRouter()
  const userStore = useUserStore()

  const designerProfileId = ref<string | null>(_designerProfileId)
  const manufacturerProfileId = ref<string | null>(_manufacturerProfileId)
  const loading = ref(false)

  /**
   * 当前用户是否拥有已认证的设计师身份
   * - 状态检查完成后使用 API 结果
   * - 未完成前 fallback 到 userStore.isDesigner (role 字段)
   */
  const isDesigner = computed(() => {
    // 始终读取两个响应式依赖，确保 Vue 正确追踪
    const checked = statusChecked.value
    const certified = designerCertified.value
    const roleBased = userStore.isDesigner
    return checked ? certified : roleBased
  })

  /**
   * 当前用户是否拥有已认证的服务商身份
   */
  const isManufacturer = computed(() => {
    const checked = statusChecked.value
    const certified = manufacturerCertified.value
    const roleBased = userStore.isManufacturer
    return checked ? certified : roleBased
  })

  /** 是否有任何公开主页可查看 */
  const hasPublicProfile = computed(() => isDesigner.value || isManufacturer.value)

  /**
   * 检查身份认证状态
   * 串行调用以避免与 request.ts 的重复请求取消机制冲突
   */
  async function checkIdentityStatus() {
    if (statusChecked.value || _statusChecking || !userStore.isLoggedIn)
      return
    _statusChecking = true

    try {
      // 串行调用，避免与页面组件的并发请求产生相同 requestKey 导致 abort
      try {
        const designerRes = await identityApi.getIdentityStatus('designer')
        designerCertified.value = designerRes.status === 'approved'
      }
      catch {
        designerCertified.value = false
      }

      try {
        const providerRes = await identityApi.getIdentityStatus('provider')
        manufacturerCertified.value = providerRes.status === 'approved'
      }
      catch {
        manufacturerCertified.value = false
      }

      // 标记检查完成 — 触发所有 computed 重新求值
      statusChecked.value = true
    }
    finally {
      _statusChecking = false
    }
  }

  /**
   * 获取设计师 profile ID（懒加载 + 缓存）
   */
  async function fetchDesignerProfileId(): Promise<string | null> {
    if (_designerProfileId) {
      designerProfileId.value = _designerProfileId
      return _designerProfileId
    }

    loading.value = true
    try {
      const res = await identityApi.getDesignerProfile()
      const id = res?.data?.id || res?.id
      if (id) {
        _designerProfileId = String(id)
        designerProfileId.value = _designerProfileId
      }
      return _designerProfileId
    }
    catch {
      console.warn('[useProfileHomepage] 获取设计师 profile ID 失败')
      return null
    }
    finally {
      loading.value = false
    }
  }

  /**
   * 获取服务商 profile ID（懒加载 + 缓存）
   */
  async function fetchManufacturerProfileId(): Promise<string | null> {
    if (_manufacturerProfileId) {
      manufacturerProfileId.value = _manufacturerProfileId
      return _manufacturerProfileId
    }

    loading.value = true
    try {
      const res = await identityApi.getProviderProfile()
      const id = res?.data?.id || res?.id
      if (id) {
        _manufacturerProfileId = String(id)
        manufacturerProfileId.value = _manufacturerProfileId
      }
      return _manufacturerProfileId
    }
    catch {
      console.warn('[useProfileHomepage] 获取服务商 profile ID 失败')
      return null
    }
    finally {
      loading.value = false
    }
  }

  /**
   * 跳转到设计师公开主页
   */
  async function goToDesignerHomepage() {
    const id = await fetchDesignerProfileId()
    if (id) {
      router.push(`/designers/${id}`)
    }
  }

  /**
   * 跳转到服务商公开主页
   */
  async function goToManufacturerHomepage() {
    const id = await fetchManufacturerProfileId()
    if (id) {
      router.push(`/providers/${id}`)
    }
  }

  /**
   * 清除缓存（退出登录时调用）
   */
  function clearCache() {
    _designerProfileId = null
    _manufacturerProfileId = null
    _statusChecking = false
    statusChecked.value = false
    designerCertified.value = false
    manufacturerCertified.value = false
    designerProfileId.value = null
    manufacturerProfileId.value = null
  }

  // 登录后延迟检查身份状态，避免与页面组件的并发请求冲突
  // request.ts 的 addPendingRequest 会 abort 相同 key 的前一个请求
  if (userStore.isLoggedIn && !statusChecked.value && !_statusChecking) {
    setTimeout(() => {
      checkIdentityStatus()
    }, 1500)
  }

  return {
    // 状态
    designerProfileId,
    manufacturerProfileId,
    loading,

    // 计算属性
    isDesigner,
    isManufacturer,
    hasPublicProfile,

    // 方法
    checkIdentityStatus,
    fetchDesignerProfileId,
    fetchManufacturerProfileId,
    goToDesignerHomepage,
    goToManufacturerHomepage,
    clearCache,
  }
}
